import { CircleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import styles from '../Input/Input.module.css';

export type FieldTexts = {
  label: string;
  helperText?: string | undefined;
  errorText?: string | undefined;
  hideLabel?: boolean | undefined;
};

export const counterIdOf = (id: string): string => `${id}-counter`;

export const describedByOf = (id: string, { helperText, errorText }: Omit<FieldTexts, 'label'>) =>
  errorText ? `${id}-error` : helperText ? `${id}-helper` : undefined;

export const controlClassName = (errorText: string | undefined, className: string | undefined): string =>
  [styles['control'], errorText ? styles['invalid'] : '', className ?? ''].join(' ');

const FieldMessage = ({ id, helperText, errorText }: Omit<FieldTexts, 'label'> & { id: string }) => {
  if (errorText)
    return (
      <span id={`${id}-error`} className={styles['error']} role="alert">
        <CircleAlert size={14} aria-hidden="true" className={styles['errorIcon']} />
        {errorText}
      </span>
    );
  if (helperText)
    return (
      <span id={`${id}-helper`} className={styles['helper']}>
        {helperText}
      </span>
    );
  return null;
};

export const Field = ({
  id,
  label,
  helperText,
  errorText,
  hideLabel = false,
  required,
  counter,
  children,
}: FieldTexts & {
  id: string;
  required?: boolean | undefined;
  counter?: string | undefined;
  children: ReactNode;
}) => (
  <div className={styles['field']}>
    <label
      htmlFor={id}
      className={
        hideLabel ? 'visually-hidden' : [styles['label'], required ? styles['required'] : ''].join(' ')
      }
    >
      {label}
    </label>
    {children}
    {errorText || helperText || counter ? (
      <div className={styles['footer']}>
        <FieldMessage id={id} helperText={helperText} errorText={errorText} />
        {counter ? (
          <span id={counterIdOf(id)} className={styles['counter']}>
            {counter}
          </span>
        ) : null}
      </div>
    ) : null}
  </div>
);
